<template>
  <div class="relative">
    <div
      class="h-16 px-6 sticky top-18 z-20 bg-slate-300 border-b border-slate-400 flex flex-grow items-center justify-between gap-4"
      :style="{ minWidth: 'calc(100vw - 22rem - 0.5rem)' }"
    >
      <div class="sticky top-18 left-94">
        <div class="flex items-center gap-4">
          <BlInputSearch
            class="w-80"
            :input="searchInput"
            @changed="onInputSearch"
          />
          <div class="w-40">
            <BlInputFilter :input="filterInput" @changed="onChangeFilter" />
          </div>
        </div>
      </div>
      <div></div>
      <div class="sticky top-18 right-6 flex items-center gap-2">
        <router-link to="client-acquisition/edit">
          <BlInputButton type="outlined" label="Edit pipeline" />
        </router-link>
        <BlInputButton
          icon="plus"
          label="Prospect"
          @clicked="newProspectOpened = true"
        />
      </div>
    </div>
    <div v-if="previewLoading" class="w-full h-92 grid place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="!prospects || !prospects.length"
      class="grid place-items-center h-92"
    >
      <BlTextBody2
        v-if="searchInput.model"
        text="Oops, the prospect not found."
        class="lg:w-128 text-slate-700 text-center"
      />
      <BlTextBody2
        v-else
        text="Oops, there are no prospect available."
        class="lg:w-128 text-slate-700 text-center"
      />
    </div>
    <div v-else class="mb-8 bg-slate-100">
      <div class="py-4 px-6 flex">
        <BlTextBody2
          class="sticky left-94"
          :text="`Showing all ${totalProspects} prospect(s)`"
        />
      </div>
      <div
        id="appointment-header"
        class="sticky top-34 flex rounded-t bg-slate-100"
        :style="{ zIndex: 11 }"
      >
        <div class="flex pl-6 bg-slate-100 sticky left-88 z-10">
          <div
            class="flex border-t border-l border-r border-slate-400 rounded-tl"
          >
            <div class="px-4 py-3 flex-shrink-0 bg-slate-200 w-20 rounded-tl">
              <BlTextSubtitle3 text="ID" class="text-slate-700" />
            </div>
            <div
              class="px-4 py-3 flex flex-shrink-0 bg-slate-200 w-60 rounded-tl"
            >
              <BlTextSubtitle3 text="Prospect name" class="text-slate-700" />
            </div>
          </div>
        </div>
        <div
          class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-40"
        >
          <BlTextSubtitle3 text="Current stage" class="text-slate-700" />
        </div>
        <div
          class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-50"
        >
          <BlTextSubtitle3 text="Assigned to" class="text-slate-700" />
        </div>
        <div
          v-if="enableBranch"
          class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-50"
        >
          <BlTextSubtitle3 text="Branch" class="text-slate-700" />
        </div>
        <div
          class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-50"
        >
          <BlTextSubtitle3 text="Tags" class="text-slate-700" />
        </div>
        <div
          v-for="stage in stages"
          :key="stage.id"
          class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-50 flex"
          :title="`Date moved to ${stage.name}`"
        >
          <BlTextSubtitle3
            :text="`Date moved to ${stage.name}`"
            class="text-slate-700 truncate"
          />
        </div>
        <div
          class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-80 flex-grow"
        >
          <BlTextSubtitle3
            text="Reason for discontinued"
            class="text-slate-700"
          />
        </div>
        <div class="flex pr-6 bg-slate-100 sticky right-0 z-10">
          <div
            class="px-4 py-3 border-t border-r border-slate-400 rounded-tr flex-shrink-0 bg-slate-200 w-23"
          ></div>
        </div>
      </div>
      <ProspectItem
        v-for="(prospect, idx) in prospects"
        :key="prospect.id"
        :item="prospect"
        :isLast="idx + 1 === prospects.length"
        @after-commit="fetchProspects"
      />
    </div>
    <div v-if="totalProspects > 25" class="flex">
      <BlInputPagination
        class="sticky left-94 my-6"
        :style="{ minWidth: 'calc(100vw - 22rem - 3rem - 0.5rem)' }"
        :page="page"
        :perPage="perPage"
        :total="totalProspects"
        @changed="changePage"
        @changed-per-page="changePerPage"
      />
    </div>
    <NewProspectModal
      v-if="newProspectOpened"
      @close-modal="newProspectOpened = false"
      @after-commit="fetchProspects"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import debounce from "lodash/debounce";

import ProspectItem from "./Item.vue";
import NewProspectModal from "../modal/NewProspectModal.vue";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputSearch from "../../BlInputSearch.vue";
import BlInputFilter from "../../BlInputFilter.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputPagination from "../../BlInputPagination.vue";

export default {
  name: "ProspectsTable",
  components: {
    ProspectItem,
    NewProspectModal,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputSearch,
    BlInputFilter,
    BlInputButton,
    BlInputPagination
  },
  data() {
    const defaultFilter = [{ start_date: "", end_date: "" }, [], [], [], []];
    return {
      previewLoading: false,
      newProspectOpened: false,
      page: 1,
      perPage: 25,
      searchInput: {
        placeholder: "Search prospect",
        model: ""
      },
      defaultFilter,
      filterInput: {
        model: defaultFilter,
        tabs: [
          { name: "Move to date", kind: "date" },
          { name: "Current stage", kind: "multi-tag", options: [] },
          {
            name: "Assigned to",
            kind: "multi",
            options: [],
            url: `/api/v1/users?sort=name_asc&status=active&role=admin&query=`,
            fetchPage: 1,
            dataKey: "users",
            countKey: "total_users",
            optionKeys: { value: "id", label: "name" }
          },
          {
            name: "Tags",
            kind: "multi",
            options: [],
            url: `/api/v1/prospect_tags?sort=name_asc&query=`,
            fetchPage: 1,
            dataKey: "prospect_tags",
            countKey: "total_count",
            optionKeys: { value: "id", label: "name" }
          }
        ]
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "stages",
      "prospects",
      "totalProspects"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    }
  },
  async mounted() {
    this.previewLoading = true;

    this.getStages().then(({ success, data }) => {
      if (!success) return;
      this.filterInput.tabs[1].options = data.map(i => {
        const res = {
          name: i.name,
          value: i.id,
          color: "#0172A3", // cornflower-8
          backgroundColor: "#E0F4FE" // cornflower-2
        };
        if (i.is_admitted) {
          res.color = "#789719"; // lime-7
          res.backgroundColor = "#F2F8CF"; //
        }
        if (i.is_pending) {
          res.color = "#D18D00"; // tulip-7
          res.backgroundColor = "#FFFDE7"; // tulip-1
        }
        if (i.is_discontinued) {
          res.color = "#F63D68"; // rose-7
          res.backgroundColor = "#FFF1F3"; // rose-2
        }
        return res;
      });
    });

    this.getBranches({ params: "?sort=name_asc" }).then(({ success, data }) => {
      if (!success) return;
      if (!this.enableBranch) return;
      const options = data.map(a => ({
        name: a.name,
        value: a.id,
        color: a.color,
        backgroundColor: a.background_color
      }));
      const tab = { name: "Branches", kind: "multi-tag", options };
      this.filterInput.tabs = [...this.filterInput.tabs, tab];
    });

    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["name"]) {
      this.searchInput.model = searchParams["name"];
    }

    const filter = [{ start_date: "", end_date: "" }, [], [], [], []];
    if (searchParams["start_date"]) {
      filter[0].start_date = searchParams["start_date"];
    }
    if (searchParams["end_date"]) {
      filter[0].end_date = searchParams["end_date"];
    }
    if (searchParams["stage_ids"]) {
      const ids = searchParams["stage_ids"].split(",");
      const arr = ids.map(i => ({ value: Number(i) }));
      filter[1] = arr;
    }
    if (searchParams["user_ids"]) {
      const ids = searchParams["user_ids"].split(",");
      const arr = ids.map(i => ({ value: Number(i) }));
      filter[2] = arr;
    }
    if (searchParams["prospect_tag_ids"]) {
      const ids = searchParams["prospect_tag_ids"].split(",");
      const arr = ids.map(i => ({ value: Number(i) }));
      filter[3] = arr;
    }
    if (searchParams["branch_ids"]) {
      const ids = searchParams["branch_ids"].split(",");
      const arr = ids.map(i => ({ value: Number(i) }));
      filter[4] = arr;
    }
    this.filterInput.model = filter;

    this.fetchProspects(true);
  },
  methods: {
    ...mapActions(["getStages", "getBranches", "getProspectStages"]),
    fetchProspects(withLoading = false) {
      if (this.$route.path !== "/client-acquisition") return;

      let params = `?page=${this.page}&per_page=${this.perPage}`;
      if (this.searchInput.model) {
        params += `&name=${this.searchInput.model}`;
      }

      const dates = this.filterInput.model.at(0) || {
        start_date: "",
        end_date: ""
      };
      const stages = this.filterInput.model.at(1) || [];
      const users = this.filterInput.model.at(2) || [];
      const tags = this.filterInput.model.at(3) || [];
      const branches = this.filterInput.model.at(4) || [];

      if (dates.start_date) {
        params += `&start_date=${dates.start_date}`;
      }
      if (dates.end_date) {
        params += `&end_date=${dates.end_date}`;
      }
      if (stages.length > 0) {
        params += `&stage_ids=${stages.map(a => a.value).join(",")}`;
      }
      if (users.length > 0) {
        params += `&user_ids=${users.map(a => a.value).join(",")}`;
      }
      if (tags.length > 0) {
        params += `&prospect_tag_ids=${tags.map(a => a.value).join(",")}`;
      }
      if (branches.length > 0) {
        params += `&branch_ids=${branches.map(a => a.value).join(",")}`;
      }

      if (withLoading) this.previewLoading = true;
      this.getProspectStages({ params }).then(async () => {
        this.previewLoading = false;
      });

      // sync url path for page
      const incomingPath = `/client-acquisition${params}&preview_type=list`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchProspects(true);
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchProspects(true);
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchProspects(true);
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchProspects(true);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/salesPipeline/prospects/Table.vue